import { useCallback, useRef } from "react";

import { usePromise } from "@touchtech/web-hooks";

import { LocalStorageKeys } from "@is/constants";
import { LocalStorageService, createSearchStylesUrl } from "@is/services";
import { ProductItem, SearchTerm, SuggestionItem } from "@is/types";
import { fetchData } from "@is/utils";

type Params = {
  terms: SearchTerm[];
  page: number;
  pageSize?: number;
};

type RequestData = {
  terms: SearchTerm[];
  deviceId: string;
  tenantId: string;
  storeId: string;
  page: number;
  pageSize: number;
  searchId: string;
};

type Response = {
  items: ProductItem[];
  totalCount: number;
  count: number;
  page: number;
  pageSize: number;
  suggestions: SuggestionItem[];
  searchId: string;
  hasNextPage: boolean;
};

type State = {
  isPending: boolean;
  error: Error | null;
  data: Response | null;
};

async function handleSearchProducts(
  data: RequestData,
  controllerRef: React.MutableRefObject<AbortController | null>
): Promise<Response> {
  const { deviceId, tenantId, storeId } = data;

  if (controllerRef?.current) {
    controllerRef?.current?.abort();
  }

  controllerRef.current = new AbortController();

  let content;

  try {
    const response = await fetchData(
      createSearchStylesUrl(),
      data,
      "POST",
      controllerRef,
      {
        "Tenant-Id": tenantId,
        "Store-Id": storeId,
        "Device-Id": deviceId,
      }
    );

    content = (await response.json()) as Response;

    if (content?.searchId) {
      LocalStorageService.setItem(LocalStorageKeys.SearchId, content.searchId);
    }
  } catch {
    content = {};
  }

  return content;
}

export const useSearchProducts = (
  abortControllerRef?: React.MutableRefObject<AbortController | null>
): [State, (params: Params) => Promise<Response>] => {
  const [{ isPending, error, data }, dispatch] = usePromise<Response>();

  const deviceId = LocalStorageService.getItem(LocalStorageKeys.DeviceId);
  const tenantId = LocalStorageService.getItem(LocalStorageKeys.TenantId);
  const storeId = LocalStorageService.getItem(LocalStorageKeys.StoreId);

  const controllerRef = useRef(null);

  const searchProducts = useCallback(
    (params: Params) =>
      dispatch(
        handleSearchProducts(
          {
            ...params,
            deviceId,
            tenantId,
            storeId,
            searchId: LocalStorageService.getItem(LocalStorageKeys.SearchId),
            pageSize: params.pageSize,
          },
          abortControllerRef === undefined ? controllerRef : abortControllerRef
        )
      ),
    [dispatch, deviceId, tenantId, storeId, abortControllerRef]
  );

  return [{ isPending, error, data }, searchProducts];
};
